import { HttpAgent } from '@ag-ui/client';
import { IAuthService } from '@core-features/core';

export const CHAT_HUB_AGENT_ID = 'paldesk';

const SSE_DATA_PREFIX = 'data: ';

/** Fields the schemas pass through, where a null is application data. */
const OPAQUE_PAYLOAD_KEYS = new Set(['delta', 'snapshot', 'rawEvent']);

function withoutRejectedNulls(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(withoutRejectedNulls);
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  return Object.fromEntries(
    Object.entries(value)
      .filter(([, entry]) => entry !== null)
      .map(([key, entry]) => [
        key,
        OPAQUE_PAYLOAD_KEYS.has(key) ? entry : withoutRejectedNulls(entry),
      ]),
  );
}

function sanitizeLine(line: string): string {
  if (!line.startsWith(SSE_DATA_PREFIX)) {
    return line;
  }

  try {
    const event: unknown = JSON.parse(line.slice(SSE_DATA_PREFIX.length));

    return SSE_DATA_PREFIX + JSON.stringify(withoutRejectedNulls(event));
  } catch {
    return line;
  }
}

/**
 * Drops nulls the AG-UI schemas reject, outside opaque payloads.
 *
 * Temporary: remove once Chat Hub omits null properties.
 */
function sanitizeEventStream(response: Response): Response {
  if (!response.body) {
    return response;
  }

  const decoder = new TextDecoder();
  const encoder = new TextEncoder();
  let buffered = '';

  const sanitizer = new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      buffered += decoder.decode(chunk, { stream: true });
      const lines = buffered.split('\n');
      buffered = lines.pop() ?? '';

      for (const line of lines) {
        controller.enqueue(encoder.encode(`${sanitizeLine(line)}\n`));
      }
    },
    flush(controller) {
      if (buffered) {
        controller.enqueue(encoder.encode(sanitizeLine(buffered)));
      }
    },
  });

  return new Response(response.body.pipeThrough(sanitizer), {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

/** Chat Hub agent that attaches a fresh access token to each request. */
export function createChatHubAgent(
  chatHubUrl: string,
  authService: IAuthService,
): HttpAgent {
  return new HttpAgent({
    url: chatHubUrl,
    fetch: async (url, requestInit) => {
      const accessToken = await authService.getAccessToken();
      const headers = new Headers(requestInit.headers);

      if (accessToken) {
        headers.set('Authorization', `Bearer ${accessToken}`);
      }

      return sanitizeEventStream(await fetch(url, { ...requestInit, headers }));
    },
  });
}
